import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';

import { disabledStatuses } from '../../constants/statuses';
import usePermissions from '../../hooks/usePermissions';
import {
	useGetFormInstanceSchemaQuery,
	useReturnToApplicantMutation,
	useUpdateAppProcessStatusMutation,
	useUpdateFormStatusMutation,
	useUpdateStageStatusMutation
} from '../../services/endpoints/formBuilderEndpoints';
import { getLocalAuth } from '../../utils/environmentUtils';
import { validateRequiredFields } from '../../utils/utilFunctions';
import EvalAssignmentsModal from '../AssignReviewModal/EvalAssignmentsModal';
import ButtonDropdown from '../core/ButtonDropdown/ButtonDropdown';
import StyledWrapper from './style';
import useFormButtonConditions from './useFormButtonConditions';

const ActionButtons = ({ type, status, item, handleAlert, application_id }) => {
	const navigate = useNavigate();
	const accountSource = useSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const path = window.location.pathname;
	const pathSegments = path.split('/');

	const appID =
		application_id || pathSegments.find((segment) => segment.includes('api-'))?.replace('api-', '');

	const applicationType = item?.appId?.split('-')?.[0];
	const uniqueID = item?.appId?.split('-')?.[1];
	const { data: apiFormData, isLoading } = useGetFormInstanceSchemaQuery(
		{
			uniqueID,
			apiToken: apiToken
		},
		{
			skip: !['afi', 'rfi'].includes(applicationType)
		}
	);
	const { form_schema: formSchema, field_values: formAnswers } = apiFormData || {};
	const normalizedApplicationStatus = (item?.app_status ?? '').toString().toLowerCase();
	const isApplicationRejected =
		normalizedApplicationStatus.includes('reject') ||
		normalizedApplicationStatus.includes('denied');
	const completelyDisabled = disabledStatuses
		?.map((status) => status?.toString().toLowerCase())
		?.includes(normalizedApplicationStatus);
	const [evalAssignmentModal, setEvalAssignmentModal] = useState(false);
	const [isDecisionsOpen, setDecisionsOpen] = useState(false);
	const [returnToApplicant] = useReturnToApplicantMutation();
	const [updateFormStatus] = useUpdateFormStatusMutation();
	const [updateStageStatus] = useUpdateStageStatusMutation();
	const [updateProcessStatus] = useUpdateAppProcessStatusMutation();
	const [buttonLoading, setButtonLoading] = useState('');
	const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejectContext, setRejectContext] = useState(null);
	const [revertWarningOpen, setRevertWarningOpen] = useState(false);
	const [revertWarningMessage, setRevertWarningMessage] = useState('');
	const { hasEditPermissions, userAccountTypeID } = usePermissions();
	const [searchParams] = useSearchParams();
	const rawStageType =
		item?.stage_type ||
		item?.form_stage?.stage_type ||
		item?.formStage?.stage_type ||
		item?.stage?.stage_type;
	const inferStageContext = (value) => {
		if (!value) {
			return null;
		}
		const text = value.toString().toLowerCase();
		if (text.includes('evaluation') || text.startsWith('esi-') || text.startsWith('esm-')) {
			return 'evaluation';
		}
		if (
			text.includes('submission') ||
			text.includes('application') ||
			text.startsWith('ssi-') ||
			text.startsWith('ssm-')
		) {
			return 'submission';
		}
		return null;
	};
	const inferContextFromStatus = (value) => {
		if (!value) {
			return null;
		}
		const text = value.toString().toLowerCase();
		if (text.includes('review') || text === 'completed') {
			return 'evaluation';
		}
		if (
			text.includes('draft') ||
			text.includes('submission') ||
			text.includes('progress') ||
			text.includes('return') ||
			text.includes('submitted') ||
			text.includes('not started')
		) {
			return 'submission';
		}
		return null;
	};
	const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';
	const canRevertDecision = Boolean(item?.can_revert_decision);
	const typeCandidate =
		typeof item?.type === 'string' && item?.type.toLowerCase().includes('stage')
			? item?.type
			: null;
	const stageContextCandidates = [
		rawStageType,
		item?.stage_type,
		typeCandidate,
		item?.stage_status,
		item?.stage_name,
		item?.stage_unique_identifier,
		item?.appId
	];
	const derivedStageContext =
		stageContextCandidates.reduce(
			(context, candidate) => context || inferStageContext(candidate),
			null
		) || inferContextFromStatus(normalizedStatus);
	const finalStatuses = [
		'pending decision',
		'rec for award',
		'rec. award',
		'on hold',
		'rejected',
		'awarded'
	];
	const showRejectAction =
		type === 'application instance' &&
		derivedStageContext &&
		hasEditPermissions &&
		!finalStatuses.includes(normalizedStatus);
	const submissionStageRejectMessage =
		'Are you sure you want to Reject this application? This application is currently available for the applicant to prepare their submission, and they will be immediately unable to modify their application once you have rejected it';
	const evaluationStageRejectMessage =
		'Are you sure you want to Reject this application? By rejecting this application now, it will no longer be available to Reviewers in the current (or future) evaluation stages';
	const resolveRejectMessage = (contextType) =>
		contextType === 'evaluation' ? evaluationStageRejectMessage : submissionStageRejectMessage;
	const activeRejectContext = (rejectContext || derivedStageContext || '').toLowerCase();
	const rejectDialogMessage = resolveRejectMessage(activeRejectContext);
	const rejectTooltipCopy =
		activeRejectContext === 'evaluation'
			? 'Reject this application and remove it from reviewer queues.'
			: 'Reject this application and prevent further applicant edits.';
	const rejectButtonKey = `${item?.appId}-${'13'}`;
	const isRejectLoading = buttonLoading === rejectButtonKey;

	const { isEditable, canComplete, canReopen } = useFormButtonConditions({
		item,
		status,
		userAccountTypeID,
		completelyDisabled,
		hasEditPermissions
	});

	const openRejectDialog = () => {
		setRejectContext(derivedStageContext);
		setRejectDialogOpen(true);
	};

	const openRevertWarning = (message) => {
		setRevertWarningMessage(
			message ||
				'This recommendation for award cannot be reverted because it has already been submitted for approval in Grants Network or has already been awarded.'
		);
		setRevertWarningOpen(true);
	};

	const closeRevertWarning = () => {
		setRevertWarningOpen(false);
		setRevertWarningMessage('');
	};

	const closeRejectDialog = () => {
		if (isRejectLoading) {
			return;
		}
		setRejectDialogOpen(false);
		setRejectContext(null);
	};

	const confirmReject = () => {
		updateStatusHandler('Denied', '13', {
			onSuccess: () => {
				setRejectDialogOpen(false);
				setRejectContext(null);
			}
		});
	};

	const updateStatusHandler = (status, alertID, callbacks = {}) => {
		const { onSuccess, onFailure } = callbacks;
		const options = {
			id: uniqueID,
			status: status,
			apiToken: apiToken
		};

		setButtonLoading(`${item?.appId}-${alertID}`);

		const handleStatusUpdateSuccess = (alertIdentifier = 0) => {
			if (alertIdentifier === '5' || alertIdentifier === '8') {
				location.reload();
			}
			setButtonLoading(``);
			handleAlert('Status Updated Successfully!', 'success');
			onSuccess?.();
		};

		const handleStatusUpdateFailure = (error) => {
			setButtonLoading(``);
			const errorMessage =
				error?.data?.message || error?.error || error?.message || error?.statusText || '';
			if (alertID === '14' || errorMessage.toLowerCase().includes('cannot be reverted')) {
				const safeMessage = errorMessage.toLowerCase().includes('cannot be reverted')
					? errorMessage
					: 'This recommendation for award cannot be reverted because it has already been submitted for approval in Grants Network or has already been awarded or rejected.';
				openRevertWarning(safeMessage);
				return;
			}
			handleAlert('Status Update Failed!', 'error');
			onFailure?.();
		};

		if (type === 'review content' || type === 'application form') {
			const completeValidationErrors = validateRequiredFields(formSchema, formAnswers, false);
			if (completeValidationErrors.length === 0) {
				updateFormStatus(options)
					.unwrap()
					.then(handleStatusUpdateSuccess)
					.catch(handleStatusUpdateFailure);
			} else {
				handleStatusUpdateFailure();
			}
		} else if (type === 'evaluation stage' && status === 'In Progress (paused)') {
			returnToApplicant({ ...options, id: appID })
				.unwrap()
				.then(handleStatusUpdateSuccess)
				.catch(handleStatusUpdateFailure);
		} else if (type === 'submission stage' || type === 'evaluation stage') {
			updateStageStatus(options)
				.unwrap()
				.then(() => handleStatusUpdateSuccess(alertID))
				.catch(handleStatusUpdateFailure);
		} else {
			updateProcessStatus({ ...options, id: appID })
				.unwrap()
				.then(handleStatusUpdateSuccess)
				.catch(handleStatusUpdateFailure);
		}
	};

	const FormButtons = () => {
		let beforeDeadline;

		if (item?.status?.includes('reopened')) {
			beforeDeadline = true;
		} else {
			if (item?.end_date) {
				beforeDeadline = new Date(item.end_date) > new Date();
			} else {
				beforeDeadline = true;
			}
		}

		return isApplicationRejected ? null : (
			<>
				{isEditable && (
					<Tooltip title="Edit Individual Form" placement="top">
						<LoadingButton
							size="small"
							variant="outlined"
							onClick={() =>
								navigate(
									`${item?.id_link}${
										searchParams.get('formtype') !== ''
											? `?formtype=${searchParams.get('formtype')}`
											: ''
									}${searchParams?.get('iFrame') === 'true' ? '&iFrame=true' : ''}`,
									{ state: { isEditing: true } }
								)
							}
						>
							Edit
						</LoadingButton>
					</Tooltip>
				)}

				{canComplete && beforeDeadline && (
					<Tooltip title="Change Form Status to Complete" placement="top">
						<LoadingButton
							size="small"
							loading={isLoading || buttonLoading === `${item?.appId}-1`}
							variant="outlined"
							onClick={() => updateStatusHandler('Completed', '1')}
						>
							Complete
						</LoadingButton>
					</Tooltip>
				)}

				{canReopen && (
					<Tooltip title="Reopen Form" placement="top">
						<LoadingButton
							size="small"
							loading={buttonLoading === `${item?.appId}-2`}
							variant="outlined"
							onClick={() => updateStatusHandler('Draft (reopened)', '2')}
						>
							Reopen
						</LoadingButton>
					</Tooltip>
				)}
			</>
		);
	};

	const ReviewStageButtons = () => (
		<>
			{status !== 'submitted' &&
				!completelyDisabled &&
				item?.all_stages_submitted &&
				hasEditPermissions && (
					<>
						<Tooltip title="Assign Reviewers to Evaluation Stage." placement="top">
							<LoadingButton
								size="small"
								variant={
									item?.is_current_stage && status === 'unassigned' ? 'contained' : 'outlined'
								}
								onClick={() => setEvalAssignmentModal(true)}
							>
								Assignments
							</LoadingButton>
						</Tooltip>
					</>
				)}

			{item?.completion_number > 0 &&
				!completelyDisabled &&
				item?.all_stages_submitted &&
				hasEditPermissions && (
					<>
						<Tooltip title="Reopen All Completed Review Forms." placement="top">
							<LoadingButton
								size="small"
								loading={buttonLoading === `${item?.appId}-${'5'}`}
								variant="outlined"
								onClick={() => updateStatusHandler('In Progress (reopened)', '5')}
							>
								Reopen All
							</LoadingButton>
						</Tooltip>
					</>
				)}

			{status === 'completed' &&
				!completelyDisabled &&
				item?.is_current_stage &&
				item?.all_stages_submitted &&
				hasEditPermissions && (
					<>
						<Tooltip title="Submit the Entire Stage." placement="top">
							<LoadingButton
								size="small"
								loading={buttonLoading === `${item?.appId}-${'3'}`}
								variant="contained"
								onClick={() => updateStatusHandler('Submitted', '3')}
							>
								Submit
							</LoadingButton>
						</Tooltip>
					</>
				)}
		</>
	);

	const SubmissionStageButtons = () => (
		<>
			{item?.completion_number > 0 &&
				!status?.includes('reopened') &&
				!completelyDisabled &&
				hasEditPermissions && (
					<>
						<Tooltip title="Reopen All Completed Application Forms" placement="top">
							<LoadingButton
								size="small"
								loading={buttonLoading === `${item?.appId}-${'8'}`}
								variant="outlined"
								onClick={() => updateStatusHandler('In Progress (reopened)', '8')}
							>
								Reopen All
							</LoadingButton>
						</Tooltip>
					</>
				)}

			{status === 'completed' &&
				!completelyDisabled &&
				item?.is_current_stage &&
				!userAccountTypeID && (
					<>
						<Tooltip title="Submit the Entire Stage." placement="top">
							<LoadingButton
								size="small"
								loading={buttonLoading === `${item?.appId}-${'6'}`}
								variant="contained"
								onClick={() => updateStatusHandler('Submitted', '6')}
							>
								Submit
							</LoadingButton>
						</Tooltip>
					</>
				)}
		</>
	);

	const DropdownDecisions = {
		title: 'Decision',
		id: 'Decisions-1',
		marginBottom: '0px',
		variant: 'contained',
		options: [
			{
				title: 'Recommend',
				onClick: () => updateStatusHandler('Rec. Award', '12')
			},
			{
				title: 'On Hold',
				onClick: () => updateStatusHandler('On Hold', '12')
			},
			{
				title: 'Reject',
				onClick: () => updateStatusHandler('Denied', '12')
			}
		]
	};
	const AppInstanceButtons = () => (
		<>
			{showRejectAction && (
				<Tooltip title={rejectTooltipCopy} placement="top">
					<LoadingButton
						size="small"
						loading={isRejectLoading}
						variant="outlined"
						onClick={openRejectDialog}
					>
						Reject
					</LoadingButton>
				</Tooltip>
			)}
			{status === 'completed' && hasEditPermissions && (
				<>
					<Tooltip title="Submit the Application for a Decision." placement="top">
						<LoadingButton
							size="small"
							loading={buttonLoading === `${item?.appId}-${'10'}`}
							variant="contained"
							onClick={() => updateStatusHandler('Pending Decision', '10')}
						>
							Submit for Decision
						</LoadingButton>
					</Tooltip>
				</>
			)}

			{status === 'pending decision' && hasEditPermissions && (
				<>
					<Tooltip title="Return the Application Process back to Completed State" placement="top">
						<LoadingButton
							size="small"
							loading={buttonLoading === `${item?.appId}-${'11'}`}
							variant="outlined"
							onClick={() => updateStatusHandler('Completed', '11')}
						>
							Revert
						</LoadingButton>
					</Tooltip>
					<ButtonDropdown
						data={DropdownDecisions}
						isOpen={isDecisionsOpen}
						customOpenToggle={setDecisionsOpen}
					/>
				</>
			)}

			{['on hold', 'denied'].includes(status) && hasEditPermissions && (
				<>
					<Tooltip
						title="Return the Application Process back to Pending Decision State"
						placement="top"
					>
						<LoadingButton
							size="small"
							loading={buttonLoading === `${item?.appId}-11`}
							variant="outlined"
							onClick={() => updateStatusHandler('Pending Decision', '11')}
						>
							Revert
						</LoadingButton>
					</Tooltip>
				</>
			)}

			{['on hold'].includes(status) && hasEditPermissions && (
				<Tooltip title="Recommend Application for Award." placement="top">
					<LoadingButton
						size="small"
						loading={buttonLoading === `${item?.appId}-12`}
						variant="contained"
						onClick={() => updateStatusHandler('Rec. Award', '12')}
					>
						Recommend
					</LoadingButton>
				</Tooltip>
			)}

			{['rec. award', 'rec for award'].includes(status) &&
				hasEditPermissions &&
				canRevertDecision && (
					<Tooltip
						title="Return the Application Process back to Pending Decision State"
						placement="top"
					>
						<LoadingButton
							size="small"
							loading={buttonLoading === `${item?.appId}-14`}
							variant="outlined"
							onClick={() => updateStatusHandler('Pending Decision', '14')}
						>
							Revert Decision
						</LoadingButton>
					</Tooltip>
				)}
		</>
	);

	return (
		<StyledWrapper.ButtonsContainer>
			{type === 'review content' || type === 'application form' ? (
				<FormButtons />
			) : type?.includes('evaluation stage') ? (
				<ReviewStageButtons />
			) : type?.includes('submission stage') ? (
				<SubmissionStageButtons />
			) : type?.includes('application instance') ? (
				<AppInstanceButtons />
			) : (
				<></>
			)}

			{evalAssignmentModal && hasEditPermissions && (
				<EvalAssignmentsModal
					item={item}
					openModal={evalAssignmentModal}
					closeModal={() => setEvalAssignmentModal(false)}
				/>
			)}

			<Dialog
				open={isRejectDialogOpen}
				onClose={() => closeRejectDialog()}
				aria-labelledby="reject-application-dialog-title"
			>
				<DialogTitle id="reject-application-dialog-title">Reject Application</DialogTitle>
				<DialogContent>
					<DialogContentText>{rejectDialogMessage}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" disabled={isRejectLoading} onClick={closeRejectDialog}>
						Cancel
					</Button>
					<LoadingButton variant="contained" onClick={confirmReject} loading={isRejectLoading}>
						Reject
					</LoadingButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={revertWarningOpen}
				onClose={closeRevertWarning}
				aria-labelledby="revert-warning-dialog-title"
			>
				<DialogTitle id="revert-warning-dialog-title">Reversion Prevented</DialogTitle>
				<DialogContent>
					<DialogContentText>{revertWarningMessage}</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center' }}>
					<Button variant="contained" onClick={closeRevertWarning}>
						Okay
					</Button>
				</DialogActions>
			</Dialog>
		</StyledWrapper.ButtonsContainer>
	);
};

export default ActionButtons;
